import type { FormEvent, ReactElement, ReactNode } from 'react'

function getAutoComplete(type: string, autocomplete?: string): string {
  if (autocomplete) {
    return autocomplete
  }

  switch (type) {
    case 'email':
      return 'email'
    case 'tel':
    case 'phone':
      return 'tel'
    case 'password':
      return 'current-password'
    case 'name':
      return 'name'
    default:
      return 'off'
  }
}

export interface InputProps {
  id?: string
  label: string
  optional?: boolean
  /** An optional element (typically a `<Tooltip>`) rendered next to the label. */
  tooltip?: ReactNode
  type?: 'text' | 'email' | 'tel' | 'password' | 'number'
  autocomplete?: string
  value: string
  placeholder?: string
  disabled?: boolean
  icon?: ReactElement | null
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  error?: boolean
  min?: number
  max?: number
  containerStyle?: string
  labelStyle?: string
  inputStyle?: string
  placeholderStyle?: string
  onInput: (value: string) => void
  onBlur?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export default function Input({
  label,
  optional,
  tooltip,
  id,
  type = 'text',
  autocomplete,
  value,
  placeholder,
  disabled = false,
  icon,
  size,
  error,
  min,
  max,
  containerStyle,
  labelStyle,
  inputStyle,
  placeholderStyle,
  onInput,
  onBlur,
  onKeyDown,
}: InputProps) {
  function handleInput(event: FormEvent<HTMLInputElement>) {
    onInput(event.currentTarget.value)
  }

  let sizePadding = ''
  let sizeText = ''
  let iconPaddingLeft = ''
  let labelTextSize = ''

  switch (size) {
    case 'xs':
      sizePadding = 'px-2.5 py-1.5'
      sizeText = 'text-xs'
      iconPaddingLeft = icon ? ' pl-8' : ' pl-2.5'
      labelTextSize = 'text-xs'
      break
    case 'sm':
      sizePadding = 'px-3 py-2'
      sizeText = 'text-sm'
      iconPaddingLeft = icon ? ' pl-9' : ' pl-3'
      labelTextSize = 'text-sm'
      break
    case 'md':
      sizePadding = 'px-3.5 py-2.5'
      sizeText = 'text-base'
      iconPaddingLeft = icon ? ' pl-10' : ' pl-3.5'
      labelTextSize = 'text-base'
      break
    case 'lg':
      sizePadding = 'px-4 py-3'
      sizeText = 'text-lg'
      iconPaddingLeft = icon ? ' pl-11' : ' pl-4'
      labelTextSize = 'text-lg'
      break
    case 'xl':
      sizePadding = 'px-5 py-3.5'
      sizeText = 'text-xl'
      iconPaddingLeft = icon ? ' pl-12' : ' pl-5'
      labelTextSize = 'text-xl'
      break
    default:
      sizePadding = 'px-3.5 py-2.5'
      sizeText = 'text-base'
      iconPaddingLeft = icon ? ' pl-10' : ' pl-3.5'
      labelTextSize = 'text-sm'
  }

  // Standard form input with label above
  const hasValue = value && value.trim().length > 0

  const baseInputStyles =
    'block w-full rounded-md text-neutral-800 shadow-sm transition-colors focus:outline-none bg-white font-sans font-extralight'
  const disabledStyles = disabled ? ' opacity-50 cursor-not-allowed' : ''

  // Border color
  let borderColor = ''
  if (error) {
    borderColor = 'border border-red-500 focus:ring-2 focus:ring-cta focus:border-transparent'
  } else {
    borderColor = 'border border-slate-400 focus:ring-2 focus:ring-cta focus:border-transparent'
  }

  // Placeholder styles
  const defaultPlaceholderStyles = 'placeholder:text-neutral-400'
  const finalPlaceholderStyles = placeholderStyle || defaultPlaceholderStyles

  const defaultInputStyles = `${baseInputStyles} ${sizePadding} ${sizeText} ${borderColor} ${finalPlaceholderStyles} sm:leading-6`
  const finalInputStyles = `${defaultInputStyles}${disabledStyles}${iconPaddingLeft} ${inputStyle || ''}`

  const defaultContainerStyle = 'flex flex-col gap-2'
  const finalContainerStyle = containerStyle || defaultContainerStyle

  const labelColor = hasValue && !disabled ? 'text-neutral-800' : 'text-neutral-400'
  const baseLabelStyles = `block font-semibold leading-6 ${labelColor} ${labelTextSize} font-sans`
  const defaultLabelStyle = labelStyle || baseLabelStyles

  const iconContainerStyles = 'absolute left-3 top-1/2 -translate-y-1/2 text-neutral-800'

  return (
    <div className={finalContainerStyle}>
      <div className='flex items-center gap-1.5'>
        <label htmlFor={id} className={defaultLabelStyle}>
          {label} {optional && <span className='text-blue-200'> (optional)</span>}
        </label>
        {tooltip}
      </div>
      <div className='relative'>
        {icon && <div className={iconContainerStyles}>{icon}</div>}
        <input
          disabled={disabled}
          type={type}
          id={id}
          autoComplete={getAutoComplete(type, autocomplete)}
          onInput={handleInput}
          onChange={handleInput}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          value={value}
          min={min}
          max={max}
          className={finalInputStyles}
          placeholder={placeholder}
        />
      </div>
    </div>
  )
}
